import React, { useState, useMemo, useEffect } from "react";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { MGridColumnDefinition } from "../MGrid";
import {
  QaTimeCardResponse,
  SchedulingTimeCardResponse,
  QaTimeCardResponseDetail,
  SchedulingTimeCardDetail,
} from "../../../interfaces/Mueller.Business.Models";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_HUNDREDTHS,
  DATA_TYPE_MONEY,
  DATA_TYPE_PERCENT_DECIMAL,
} from "typeConstants/dataTypes";
import { normalizeNumberVariableDecimal, formatNumberConsistentDecimalLength } from "components/FormElements";
import { moneyFormatter, formatDecimalPercent2 } from "components/Shared/Formatters";
import { transformEmployeeList } from "./Definitions.js";

export const expandCollapseColWidth = 40;

export enum SortDirection {
  ASC = "ASC",
  DES = "DES",
  NONE = "NONE",
}

export enum ExpandCollapseAllStates {
  EXPAND = "EXPAND",
  COLLAPSE = "COLLAPSE",
}

export interface TimeLogTableGroupProps {
  columnDefinitions: MGridColumnDefinition[];
  data: QaTimeCardResponse | SchedulingTimeCardResponse;
  sort?: SortedColumn;
  expandCollapseState?: ExpandCollapseAllStates;
  onRowClick: (internetUserId: number) => void;
}

export interface SortedColumn {
  key: string;
  direction: SortDirection;
  dataType: string;
}

export const getValueByPath = (obj: object, path: string) =>
  path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

export const getRowValue = (data: object, property: string, dataType: string) => {
  let value = getValueByPath(data, property);
  if (dataType) {
    const formatter = getFormatterForDataType(dataType);
    return formatter(value);
  }
  return value;
};

export const compareWithObjects = (a: any, b: any, keys: string) => {
  const aValue = getValueByPath(a, keys);
  const bValue = getValueByPath(b, keys);

  if (aValue === undefined || bValue === undefined) {
    return aValue === bValue ? 0 : aValue === undefined ? -1 : 1;
  }

  if (typeof aValue === "number" && typeof bValue === "number") {
    return aValue - bValue;
  }

  return String(aValue).localeCompare(String(bValue));
};

// TODO: This should go some where that could be referenced by both MGrid and this component
export const getFormatterForDataType = (dataType: string) => {
  // Only supporting types here that are used in TimeLogs/Definitions.js
  switch (dataType) {
    case DATA_TYPE_NUMBER:
      return normalizeNumberVariableDecimal();
    case DATA_TYPE_NUMBER_HUNDREDTHS:
      return formatNumberConsistentDecimalLength();
    case DATA_TYPE_MONEY:
      return moneyFormatter(2);
    case DATA_TYPE_PERCENT_DECIMAL:
      return formatDecimalPercent2(0, true);
  }
};

const TimeLogTableGroup = (props: TimeLogTableGroupProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const visibleColumnDefs = props.columnDefinitions?.filter((col) => col.visible);

  useEffect(() => {
    if (props.expandCollapseState) {
      switch (props.expandCollapseState) {
        case ExpandCollapseAllStates.EXPAND:
          if (!expanded) setExpanded(true);
          break;
        case ExpandCollapseAllStates.COLLAPSE:
          if (expanded) setExpanded(false);
          break;
      }
    }
  }, [props.expandCollapseState]);

  const toggleExpanded = () => setExpanded(!expanded);

  const headerRow = useMemo(() => {
    // Add 1 to the width due to the expand/collapse column
    const colSpanWidth =
      (visibleColumnDefs?.slice(0, 3).reduce((acc, val) => acc + val.width, 0) ?? 0) + expandCollapseColWidth;
    return (
      <TableRow>
        {/* Add an extra column for the expand/collapse control */}
        <TableCell style={{ textAlign: "center" }}>
          <Icon color="primary" onClick={toggleExpanded}>
            {expanded ? "remove" : "add"}
          </Icon>
        </TableCell>
        {/* Header group spans the first 4 columns. The width is determined by colgroup. */}
        <TableCell colSpan={4}>
          {props.data?.totals?.employeeName?.firstLast}
        </TableCell>
        {visibleColumnDefs?.slice(4).map(({ key, tooltip, dataSource, dataType }, i) => (
          <TableCell
            // @ts-ignore
            title={typeof tooltip === "string" ? tooltip : null}
            key={`${key}-${i}`}
            style={{ textAlign: `${dataType ? "right" : "left"}` }}
          >
            {typeof dataSource === "function"
              ? dataSource(props.data?.totals)
              : getRowValue(props.data?.totals, dataSource, dataType)}
          </TableCell>
        ))}
      </TableRow>
    );
  }, [props.columnDefinitions, props.data, expanded]);

  const tableRows = useMemo(() => {
    if (!expanded) return <></>;

    const rowData = transformEmployeeList([...props.data?.employeeList]);

    if (props.sort && props.sort?.direction !== SortDirection.NONE) {
      const comparer = (
        a: QaTimeCardResponseDetail | SchedulingTimeCardDetail,
        b: QaTimeCardResponseDetail | SchedulingTimeCardDetail,
      ) => {
        // If there's no datatype its a string
        switch (props.sort.direction) {
          case SortDirection.ASC:
            return compareWithObjects(a, b, props.sort.key);
          case SortDirection.DES:
            return compareWithObjects(b, a, props.sort.key);
          case SortDirection.NONE:
          default:
            return 0;
        }
      };
      rowData.sort(comparer);
    }

    const rows = rowData.map((row: SchedulingTimeCardDetail | QaTimeCardResponseDetail, rowIndex: number) => (
      <TableRow onClick={() => props.onRowClick(row.internetUserId)} key={`${row.internetUserId}-${rowIndex}`}>
        {/* Empty cell for expand collapse column */}
        <TableCell className="m-table-data-column" />
        {visibleColumnDefs?.map(({ key, tooltip, dataSource, dataType }, colIndex) => (
          <TableCell
            // @ts-ignore
            title={typeof tooltip === "function" ? tooltip(row) : tooltip}
            className="m-table-data-column"
            key={`${key}-${rowIndex}-${colIndex}`}
            style={{ textAlign: `${dataType ? "right" : "left"}` }}
          >
            {typeof dataSource === "function" ? dataSource(row) : getRowValue(row, dataSource, dataType)}
          </TableCell>
        ))}
      </TableRow>
    ));

    return rows;
  }, [props.columnDefinitions, props.data, props.sort, expanded]);

  return (
    <TableContainer component="div" style={{ fontSize: "12px", overflowX: "visible" }}>
      <Table style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: `${expandCollapseColWidth}px` }} />
          {visibleColumnDefs?.map((col, i) => (
            <col key={`col-${col.key}-${i}`} style={{ width: `${col.width}px` }} />
          ))}
        </colgroup>
        <TableHead>{headerRow}</TableHead>
        <TableBody>{tableRows}</TableBody>
      </Table>
    </TableContainer>
  );
};

export default TimeLogTableGroup;
